import { ChangeEvent, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { ControllerRenderProps, useController, useForm } from 'react-hook-form';
import { METAL_TYPE, IBuyerSignUpData, IBuyerSignUpKeys } from '@frontend/api';
import { Translate } from '@frontend/translation';
import {
  Button,
  CheckBox,
  Select,
  TextField,
  TextArea,
} from '@frontend/ui-elements';
import { yupResolver } from '@hookform/resolvers/yup';
import { SelectChangeEvent } from '@mui/material';
import {
  ButtonsBox,
  DualBox,
  FieldsWrapper,
  FormBox,
  FormContainer,
} from '../SignUpForms/SignUpForms.css';
import FormType from '@customer-platform-shared/components/Forms/utils/FormType.types';
import PhoneField from '@customer-platform-shared/components/ui-csr/PhoneField/PhoneField';
import { useFormTracking } from '@customer-platform-shared/components/Forms/utils/useFormTracking';
import { buildSelectDataByOptionsAndNamespace } from '@customer-platform-shared/components/Forms/utils/utils';
import {
  MAX_NUMBER_AFTER_DECIMAL,
  MIN_QUANTITY_REQUIRED_MT,
} from '@customer-platform-shared/components/Forms/Forms.const';
import getBuyerSignUpFormSchema from './BuyerSignUpForm.utils';
import { BuyerSignUpFormProps } from './BuyerSignUpForm.types';

export default function BuyerSignUpForm({
  errors,
  isSuccess,
  isPending,
  onSubmit,
}: BuyerSignUpFormProps) {
  const intl = useIntl();

  const formId = 'buyer-signup-form.form';

  const required = intl.formatMessage({
    id: 'customer-platform.shared.components.forms.errors.required',
  });
  const validPhoneNumber = intl.formatMessage({
    id: 'customer-platform.shared.components.forms.errors.phone_number.invalid',
  });
  const validEmail = intl.formatMessage({
    id: 'customer-platform.shared.components.forms.errors.email.invalid',
  });
  const maxNumbersAfterDecimal = intl.formatMessage(
    {
      id: 'customer-platform.shared.components.forms.errors.number.max_numbers_after_decimal',
    },
    { MAX_NUMBER_AFTER_DECIMAL: MAX_NUMBER_AFTER_DECIMAL.toString() },
  );
  const minQuantityRequired = intl.formatMessage(
    {
      id: 'customer-platform.shared.components.forms.errors.quantify.min-quantity-required',
    },
    { MIN_QUANTITY_REQUIRED_MT: MIN_QUANTITY_REQUIRED_MT.toString() },
  );

  const buyerSignUpFormSchema = getBuyerSignUpFormSchema({
    required,
    maxNumbersAfterDecimal,
    minQuantityRequired,
    validPhoneNumber,
    validEmail,
  });
  const {
    control,
    handleSubmit,
    register,
    reset,
    setError,
    clearErrors,
    formState: { errors: formErrors },
  } = useForm<FormType<IBuyerSignUpData>>({
    defaultValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      quantity: undefined,
      email: '',
      phoneNumber: '',
      metalType: '',
      productDescription: '',
      whatsappConsent: false,
    },
    resolver: yupResolver<FormType<IBuyerSignUpData>>(buyerSignUpFormSchema),
  });

  const { field: metalTypeField } = useController({
    name: 'metalType',
    control,
  });

  const metalTypeData = buildSelectDataByOptionsAndNamespace(METAL_TYPE, intl);

  const handleStringChange =
    (field: ControllerRenderProps) => (value: string) => {
      field.onChange(value);
    };

  const handleSelectChange =
    (field: ControllerRenderProps) => (event: SelectChangeEvent<unknown>) => {
      field.onChange(event.target.value);
    };

  const { field: phoneNumberField } = useController({
    name: 'phoneNumber',
    control,
  });

  const { field: whatsappConsentField } = useController({
    name: 'whatsappConsent',
    control,
  });

  const handleCheckBoxChange = (event: ChangeEvent<HTMLInputElement>) => {
    whatsappConsentField.onChange(event.target.checked);
  };

  useEffect(() => {
    if (!errors || Object.keys(errors).length === 0) {
      clearErrors();
      return;
    }
    Object.entries(errors).forEach(([field, messages]) =>
      setError(field as IBuyerSignUpKeys, {
        type: 'manual',
        message: (messages?.[0] ?? '') as string,
      }),
    );
  }, [errors, setError, clearErrors]);

  useFormTracking({ formId, formErrors, isSuccess });

  useEffect(() => {
    if (isSuccess) {
      reset();
    }
  }, [reset, isSuccess]);

  return (
    <FormContainer>
      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        id={formId}
        name={formId}
      >
        <FormBox>
          <FieldsWrapper>
            <Select
              id="metal-type"
              label={
                <Translate id="customer-platform.shared.components.forms.request-form.select.metal.type" />
              }
              options={metalTypeData.options}
              value={metalTypeField.value}
              onChange={handleSelectChange(
                metalTypeField as ControllerRenderProps,
              )}
              error={!!formErrors?.metalType}
              helperText={formErrors?.metalType?.message}
              required
              formId={formId}
            />

            <TextField
              id="quantity"
              type="number"
              label={
                <Translate id="customer-platform.shared.components.forms.request-form.textfield.quantity" />
              }
              {...register('quantity')}
              error={!!formErrors?.quantity}
              helperText={formErrors?.quantity?.message}
              required
              formId={formId}
            />

            <DualBox>
              <TextField
                id="first-name"
                label={
                  <Translate id="customer-platform.signup.forms.buyer-signup.textfield.first_name" />
                }
                {...register('firstName')}
                error={!!formErrors?.firstName}
                helperText={formErrors?.firstName?.message}
                required
                formId={formId}
              />
              <TextField
                id="last-name"
                label={
                  <Translate id="customer-platform.signup.forms.buyer-signup.textfield.last_name" />
                }
                {...register('lastName')}
                error={!!formErrors?.lastName}
                helperText={formErrors?.lastName?.message}
                required
                formId={formId}
              />
            </DualBox>

            <TextField
              id="company-name"
              label={
                <Translate id="customer-platform.signup.forms.buyer-signup.textfield.company_name" />
              }
              {...register('companyName')}
              error={!!formErrors?.companyName}
              helperText={formErrors?.companyName?.message}
              required
              formId={formId}
            />
            <TextField
              id="email"
              type="email"
              label={
                <Translate id="customer-platform.signup.forms.buyer-signup.textfield.email" />
              }
              {...register('email')}
              error={!!formErrors?.email}
              helperText={formErrors?.email?.message}
              required
              formId={formId}
            />
            <PhoneField
              id="phone-number"
              label={
                <Translate id="customer-platform.signup.forms.buyer-signup.textfield.phone_number" />
              }
              defaultCountry="DE"
              value={phoneNumberField.value}
              onChange={handleStringChange(
                phoneNumberField as ControllerRenderProps,
              )}
              error={!!formErrors.phoneNumber}
              helperText={formErrors?.phoneNumber?.message}
              formId={formId}
              required
            />
            <TextArea
              id="product-description"
              data-testid="product-description"
              placeholder={intl.formatMessage({
                id: 'customer-platform.signup.forms.buyer-signup.textfield.product_description',
              })}
              {...register('productDescription')}
              error={!!formErrors?.productDescription}
              helperText={formErrors?.productDescription?.message}
              formId={formId}
              minRows={2.25}
            />

            <CheckBox
              id="whatsapp-consent"
              label={
                <Translate id="customer-platform.signup.forms.buyer-signup.checkbox.whatsapp_consent" />
              }
              value={whatsappConsentField.value as boolean}
              onChange={handleCheckBoxChange}
              error={!!formErrors?.whatsappConsent}
              helperText={formErrors?.whatsappConsent?.message}
              formId={formId}
              size="small"
            />
          </FieldsWrapper>

          <ButtonsBox>
            <Button
              buttonType="primary"
              id="buyer-form.button.submit"
              data-testid="buyer-form.button.submit"
              size="large"
              type="submit"
              isSubmitting={isPending}
            >
              <Translate id="customer-platform.signup.cta" />
            </Button>
          </ButtonsBox>
        </FormBox>
      </form>
    </FormContainer>
  );
}
